// src/payment/hooks/usePaymentHandler.js
import { useCallback, useState, useRef } from 'react';
import { PaymentService } from '../core/PaymentService';
import { QRAdapter } from '../adapters/QRAdapter';
import { StripeAdapter } from '../adapters/StripeAdapter';

export const usePaymentHandler = (slug, resumen, onSuccess) => {
  const [loading, setLoading] = useState({
    generating: false,
    verifying: false,
    registering: false,
  });
  const [error, setError] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const qrAdapterRef = useRef(null);
  const stripeAdapterRef = useRef(null);

  // ==========================================
  // QR: GENERAR
  // ==========================================
  const generateQR = useCallback(async (onProgress) => {
    setLoading(prev => ({ ...prev, generating: true }));
    setError(null);

    try {
      const adapter = new QRAdapter(slug, resumen, onProgress);
      qrAdapterRef.current = adapter;
      const data = await adapter.generate();
      setQrData(data);
      setPaymentStatus('qr_generated');
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Error al generar QR';
      setError(errorMsg);
      setPaymentStatus('error');
      throw new Error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, generating: false }));
    }
  }, [slug, resumen]);

  // ==========================================
  // QR: POLLING
  // ==========================================
  const startPolling = useCallback(async (qrId, onComplete) => {
    setLoading(prev => ({ ...prev, verifying: true }));
    setPaymentStatus('verifying');
    
    return new Promise((resolve, reject) => {
      const adapter = qrAdapterRef.current || new QRAdapter(slug, resumen);
      qrAdapterRef.current = adapter;
      
      adapter.startPolling(qrId, 
        async () => {
          setLoading(prev => ({ ...prev, verifying: false }));
          try {
            const service = new PaymentService(slug, resumen);
            const result = await service.registerFormData('qr', qrId);
            setPaymentStatus('success');
            onComplete?.();
            resolve(result);
          } catch (err) {
            setPaymentStatus('error');
            reject(err);
          }
        },
        (err) => {
          setLoading(prev => ({ ...prev, verifying: false }));
          const errorMsg = err.message || 'Error verificando pago';
          setError(errorMsg);
          setPaymentStatus('error');
          reject(new Error(errorMsg));
        }
      );
    });
  }, [slug, resumen]);

  // ==========================================
  // STRIPE: INICIALIZAR (CREA ADAPTER Y CLIENT SECRET)
  // ==========================================
  const initializeStripe = useCallback(async () => {
    setLoading(prev => ({ ...prev, generating: true }));
    setError(null);

    try {
      const adapter = new StripeAdapter(slug, resumen);
      stripeAdapterRef.current = adapter;
      
      await adapter.initialize();
      
      const secret = await adapter.createPaymentIntent();
      setClientSecret(secret);
      setPaymentStatus('stripe_ready');
      
      console.log('[usePaymentHandler] Stripe inicializado, clientSecret:', !!secret);
      
      return { adapter, clientSecret: secret };
    } catch (err) {
      const errorMsg = err.message || 'Error inicializando Stripe';
      setError(errorMsg);
      setPaymentStatus('error');
      throw new Error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, generating: false }));
    }
  }, [slug, resumen]);

  // ==========================================
  // STRIPE: CONFIRMAR PAGO (recibe clientSecret como parámetro)
  // ==========================================
  const confirmStripePayment = useCallback(async (cardElement, onComplete, stripeInstance) => {
    setLoading(prev => ({ ...prev, registering: true }));
    setError(null);
    setPaymentStatus('processing');
    
    try {
      let adapter = stripeAdapterRef.current;
      if (!adapter) {
        adapter = new StripeAdapter(slug, resumen);
        stripeAdapterRef.current = adapter;
        if (stripeInstance) {
          adapter.stripe = stripeInstance;
          adapter._initialized = true;
        } else {
          await adapter.initialize();
        }
      }

      let secret = clientSecret;
      if (!secret) {
        console.log('[usePaymentHandler] No hay clientSecret, creando uno...');
        secret = await adapter.createPaymentIntent();
        setClientSecret(secret);
      }

      const paymentIntent = await adapter.confirmPayment(secret, cardElement);
      
      if (paymentIntent.status === 'succeeded') {
        const service = new PaymentService(slug, resumen);
        await service.registerFormData('stripe', null, paymentIntent.id);
        setPaymentStatus('success');
        onComplete?.();
        setLoading(prev => ({ ...prev, registering: false }));
        return paymentIntent;
      } else {
        throw new Error(`Estado de pago inesperado: ${paymentIntent.status}`);
      }
    } catch (err) {
      const errorMsg = err.message || 'Error al procesar el pago';
      setError(errorMsg);
      setPaymentStatus('error');
      setLoading(prev => ({ ...prev, registering: false }));
      throw new Error(errorMsg);
    }
  }, [slug, resumen, clientSecret]);

  // ==========================================
  // STRIPE: FLUJO COMPLETO (TODO EN UNO)
  // ==========================================
  // 🔧 Ahora acepta `stripeInstance`: la misma instancia de Stripe que ya
  // está viva en el <Elements> del componente (obtenida vía useStripe()),
  // para no crear una instancia nueva y romper la confirmación del pago.
  const processStripePayment = useCallback(async (cardElement, onComplete, stripeInstance) => {
    setLoading(prev => ({ ...prev, generating: true, registering: true }));
    setError(null);
    setPaymentStatus('processing');
    
    try {
      const adapter = new StripeAdapter(slug, resumen);
      stripeAdapterRef.current = adapter;
      
      const paymentIntent = await adapter.createAndConfirmPayment(cardElement, stripeInstance);
      
      if (paymentIntent.status === 'succeeded') {
        const service = new PaymentService(slug, resumen);
        await service.registerFormData('stripe', null, paymentIntent.id);
        setPaymentStatus('success');
        onComplete?.();
        setLoading(prev => ({ ...prev, generating: false, registering: false }));
        return paymentIntent;
      } else {
        throw new Error(`Estado de pago inesperado: ${paymentIntent.status}`);
      }
    } catch (err) {
      const errorMsg = err.message || 'Error al procesar el pago';
      setError(errorMsg);
      setPaymentStatus('error');
      setLoading(prev => ({ ...prev, generating: false, registering: false }));
      throw new Error(errorMsg);
    }
  }, [slug, resumen]);

  // ==========================================
  // REGISTRO DIRECTO
  // ==========================================
  const registerData = useCallback(async (paymentMethod, qrId = null, paymentIntentId = null) => {
    setLoading(prev => ({ ...prev, registering: true }));
    setError(null);
    
    try {
      const service = new PaymentService(slug, resumen);
      // 🟢 FIX: antes decía `paymentIntentId = null` (una ASIGNACIÓN dentro
      // de la llamada), lo que pisaba el valor recibido y siempre mandaba
      // null sin importar qué paymentIntentId te hayan pasado.
      const result = await service.registerFormData(paymentMethod, qrId, paymentIntentId);
      setPaymentStatus('success');
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Error al registrar datos';
      setError(errorMsg);
      setPaymentStatus('error');
      throw new Error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, registering: false }));
    }
  }, [slug, resumen]);

  // ==========================================
  // RESET
  // ==========================================
  const reset = useCallback(() => {
    if (qrAdapterRef.current) {
      qrAdapterRef.current.stopPolling();
      qrAdapterRef.current = null;
    }
    stripeAdapterRef.current = null;
    setLoading({ generating: false, verifying: false, registering: false });
    setError(null);
    setQrData(null);
    setClientSecret(null);
    setPaymentStatus(null);
  }, []);

  // ==========================================
  // STOP POLLING
  // ==========================================
  const stopPolling = useCallback(() => {
    if (qrAdapterRef.current) {
      qrAdapterRef.current.stopPolling();
      qrAdapterRef.current = null;
    }
    setLoading(prev => ({ ...prev, verifying: false }));
  }, []);

  

  return {
    loading,
    error,
    qrData,
    clientSecret,
    paymentStatus,
    generateQR,
    startPolling,
    initializeStripe,
    confirmStripePayment,
    processStripePayment,
    registerData,
    reset,
    stopPolling,
  };
};
